// src/components/superuser/ClientSettings.js
//
// Client-level settings for the superuser. Everything persists on the existing
// Clients/{clientId} doc (property-level settings stay in PropertySettings).
// One listener loads the client doc once; each tab has its own Save button that
// merges just its slice, so tabs are independent and low-risk.
//
// Tabs: Company & Branding · Health Weights · Notifications & Reports ·
//       Approvals · Property Defaults · Billing Contacts

import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  createFilterOptions,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import TuneIcon from "@mui/icons-material/Tune";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CategoryIcon from "@mui/icons-material/Category";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { collection, onSnapshot, query, setDoc, where } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "../../firebase";
import { clientDoc } from "../../utils/firestorePaths";
import PropertyContext from "../../contexts/PropertyContext";
import { DEFAULT_WEIGHTS } from "../../utils/portfolioHealth";
import { STATUS_OPTIONS } from "../../constants/roomConstants";

const TIMEZONES = [
  { label: "Eastern (ET) — New York", value: "America/New_York" },
  { label: "Central (CT) — Chicago", value: "America/Chicago" },
  { label: "Mountain (MT) — Denver", value: "America/Denver" },
  { label: "Mountain (MT) — Phoenix (no DST)", value: "America/Phoenix" },
  { label: "Pacific (PT) — Los Angeles", value: "America/Los_Angeles" },
  { label: "Alaska (AKT) — Anchorage", value: "America/Anchorage" },
  { label: "Hawaii (HT) — Honolulu", value: "Pacific/Honolulu" },
];

// Health-weight fields with human labels (keys match portfolioHealth weights).
const WEIGHT_FIELDS = [
  { key: "downPct", label: "Down-rooms weight", help: "Points at 100% of rooms down" },
  { key: "openWO", label: "Open work order", help: "Points per open work order" },
  { key: "openIssue", label: "Open issue", help: "Points per open property issue" },
  { key: "needsVendor", label: "Vendor-needed room", help: "Points per vendor-needed room" },
  { key: "stale", label: "Stale penalty", help: "Points when no recent activity" },
  { key: "staleHours", label: "Stale after (hours)", help: "Hours before a property counts as stale" },
];

// Search recipients by both name and email while chips show just the name.
const recipientFilter = createFilterOptions({
  stringify: (o) => `${o.name || ""} ${o.email || ""}`,
});

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

export default function ClientSettings(props) {
  const ctx = useContext(PropertyContext);
  const clientId = ctx.selectedClient || props.selectedClient || props.clientId;

  const [tab, setTab] = useState(0);
  const [savingKey, setSavingKey] = useState(null);
  const [snack, setSnack] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Company & Branding
  const [name, setName] = useState("");
  const [timezoneDefault, setTimezoneDefault] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  // Health weights
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);

  // Notifications & Reports
  const [reportRecipients, setReportRecipients] = useState([]); // [{id,name,email}]
  const [clientUsers, setClientUsers] = useState([]); // selectable accounts
  const [dailyOps, setDailyOps] = useState(false);
  const [weeklyPortfolio, setWeeklyPortfolio] = useState(false);
  const [notifyGuestWO, setNotifyGuestWO] = useState(false);
  const [notifyNoActivity, setNotifyNoActivity] = useState(false);

  // Approvals
  const [poThreshold, setPoThreshold] = useState("");
  const [partsThreshold, setPartsThreshold] = useState("");

  // Property Defaults
  const [roomStatuses, setRoomStatuses] = useState([]);
  const [issueCategories, setIssueCategories] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);

  // Billing Contacts
  const [contacts, setContacts] = useState([]);

  // Load the client doc once; hydrate local state a single time so an incoming
  // snapshot (e.g. after saving one tab) can't clobber unsaved edits on another.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      clientDoc(clientId),
      (doc) => {
        if (hydratedRef.current) return;
        const c = doc.data() || {};
        setName(c.name || "");
        setTimezoneDefault(c.timezoneDefault || "");
        setLogoUrl(c.logoUrl || "");
        setWeights({ ...DEFAULT_WEIGHTS, ...(c.attentionWeights || {}) });
        // Normalize recipients: keep objects, upgrade any legacy plain-email
        // strings into {id,name,email} so chips still render a label.
        setReportRecipients(
          (c.reportRecipients || []).map((r) =>
            typeof r === "string" ? { id: r, name: r, email: r } : r
          )
        );
        setDailyOps(!!c.scheduledReports?.dailyOps);
        setWeeklyPortfolio(!!c.scheduledReports?.weeklyPortfolio);
        setNotifyGuestWO(!!c.notificationRules?.notifyGuestWO);
        setNotifyNoActivity(!!c.notificationRules?.notifyNoActivity);
        setPoThreshold(
          c.approvalThresholds?.purchaseOrder != null
            ? String(c.approvalThresholds.purchaseOrder)
            : ""
        );
        setPartsThreshold(
          c.approvalThresholds?.parts != null
            ? String(c.approvalThresholds.parts)
            : ""
        );
        setRoomStatuses(c.defaults?.roomStatuses || []);
        setIssueCategories(c.defaults?.issueCategories || []);
        setItemCategories(c.defaults?.itemCategories || []);
        setContacts(c.billing?.contacts || []);
        hydratedRef.current = true;
      },
      (err) => console.error("client settings listener", err)
    );
    return unsub;
  }, [clientId]);

  // Client's user accounts — options for the notification-recipient picker.
  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(collection(firestore, "users"), where("clientId", "==", clientId)),
      (snap) =>
        setClientUsers(
          snap.docs.map((d) => {
            const u = d.data();
            return {
              id: d.id,
              name: u.fullName || u.userName || u.username || u.personalEmail || d.id,
              email: u.personalEmail || u.email || "",
            };
          })
        ),
      (err) => console.error("client users listener", err)
    );
    return unsub;
  }, [clientId]);

  const save = async (key, patch) => {
    if (!clientId) return;
    setSavingKey(key);
    try {
      await setDoc(clientDoc(clientId), patch, { merge: true });
      setSnack({ severity: "success", message: "Settings saved" });
    } catch (err) {
      console.error("save settings failed", err);
      setSnack({ severity: "error", message: "Could not save settings" });
    }
    setSavingKey(null);
  };

  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const handleLogo = async (file) => {
    if (!file || !clientId) return;
    setLogoUploading(true);
    try {
      const r = storageRef(storage, `Clients/${clientId}/branding/logo`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      setLogoUrl(url);
      await setDoc(clientDoc(clientId), { logoUrl: url }, { merge: true });
      setSnack({ severity: "success", message: "Logo updated" });
    } catch (err) {
      console.error("logo upload failed", err);
      setSnack({ severity: "error", message: "Logo upload failed" });
    }
    setLogoUploading(false);
  };

  const saveBtn = (key, onClick) => (
    <Button variant="contained" onClick={onClick} disabled={savingKey === key}>
      {savingKey === key ? "Saving…" : "Save Changes"}
    </Button>
  );

  if (!clientId) {
    return (
      <Typography color="text.secondary" sx={{ mt: 4 }}>
        No client is associated with this account.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Client Settings
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Company & Branding" />
          <Tab icon={<TuneIcon />} iconPosition="start" label="Health Weights" />
          <Tab icon={<NotificationsIcon />} iconPosition="start" label="Notifications & Reports" />
          <Tab icon={<FactCheckIcon />} iconPosition="start" label="Approvals" />
          <Tab icon={<CategoryIcon />} iconPosition="start" label="Property Defaults" />
          <Tab icon={<ContactMailIcon />} iconPosition="start" label="Billing Contacts" />
        </Tabs>
      </Box>

      {/* 1 · Company & Branding */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2} sx={{ maxWidth: 640 }}>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar src={logoUrl} variant="rounded" sx={{ width: 64, height: 64 }}>
              {(name || "C").charAt(0)}
            </Avatar>
            <Button
              component="label"
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              disabled={logoUploading}
            >
              {logoUploading ? "Uploading…" : "Upload Logo"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleLogo(e.target.files?.[0])}
              />
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company / Client Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormControl fullWidth>
              <InputLabel>Default Timezone</InputLabel>
              <Select
                label="Default Timezone"
                value={timezoneDefault}
                onChange={(e) => setTimezoneDefault(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select timezone</em>
                </MenuItem>
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {saveBtn("company", () =>
              save("company", { name, timezoneDefault })
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* 2 · Health Weights */}
      <TabPanel value={tab} index={1}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
          These weights drive each property's health score — the globe point
          colors and the "Properties Needing Attention" ranking on the dashboard.
          Higher weight = a bigger hit to health.
        </Typography>
        <Grid container spacing={2} sx={{ maxWidth: 640 }}>
          {WEIGHT_FIELDS.map((f) => (
            <Grid item xs={12} sm={6} key={f.key}>
              <TextField
                fullWidth
                type="number"
                label={f.label}
                value={weights[f.key] ?? ""}
                onChange={(e) =>
                  setWeights((w) => ({ ...w, [f.key]: num(e.target.value) }))
                }
                helperText={f.help}
                inputProps={{ step: "any" }}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              {saveBtn("weights", () =>
                save("weights", { attentionWeights: weights })
              )}
              <Button onClick={() => setWeights(DEFAULT_WEIGHTS)}>
                Reset to defaults
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 3 · Notifications & Reports */}
      <TabPanel value={tab} index={2}>
        <Grid container spacing={2} sx={{ maxWidth: 640 }}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={clientUsers}
              value={reportRecipients}
              onChange={(e, val) => setReportRecipients(val)}
              filterOptions={recipientFilter}
              getOptionLabel={(o) => o.name || o.email || o.id}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterSelectedOptions
              noOptionsText="No matching users"
              renderOption={(liProps, o) => (
                <Box component="li" {...liProps} key={o.id}>
                  <Box>
                    <Typography variant="body2">{o.name}</Typography>
                    {o.email && (
                      <Typography variant="caption" color="text.secondary">
                        {o.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((o, index) => (
                  <Chip
                    size="small"
                    label={o.name || o.email}
                    {...getTagProps({ index })}
                    key={o.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Notification Recipients"
                  placeholder="Search users…"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Scheduled Reports
            </Typography>
            <FormControlLabel
              control={
                <Switch checked={dailyOps} onChange={(e) => setDailyOps(e.target.checked)} />
              }
              label="Daily operations summary"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={weeklyPortfolio}
                  onChange={(e) => setWeeklyPortfolio(e.target.checked)}
                />
              }
              label="Weekly portfolio summary"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Notification Rules
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={notifyGuestWO}
                  onChange={(e) => setNotifyGuestWO(e.target.checked)}
                />
              }
              label="Notify on new guest work order"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifyNoActivity}
                  onChange={(e) => setNotifyNoActivity(e.target.checked)}
                />
              }
              label="Notify when a property has no activity for a day"
            />
          </Grid>
          <Grid item xs={12}>
            {saveBtn("notifications", () =>
              save("notifications", {
                reportRecipients,
                scheduledReports: { dailyOps, weeklyPortfolio },
                notificationRules: { notifyGuestWO, notifyNoActivity },
              })
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* 4 · Approvals */}
      <TabPanel value={tab} index={3}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
          Spend at or above these amounts will require superuser approval in the
          Approvals queue.
        </Typography>
        <Grid container spacing={2} sx={{ maxWidth: 640 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Purchase order threshold"
              value={poThreshold}
              onChange={(e) => setPoThreshold(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Parts request threshold"
              value={partsThreshold}
              onChange={(e) => setPartsThreshold(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {saveBtn("approvals", () =>
              save("approvals", {
                approvalThresholds: {
                  purchaseOrder: num(poThreshold),
                  parts: num(partsThreshold),
                },
              })
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* 5 · Property Defaults */}
      <TabPanel value={tab} index={4}>
        <Alert severity="info" sx={{ mb: 2, maxWidth: 640 }}>
          These defaults are stored on the client, but aren't yet applied when
          creating a property — that wiring is a follow-up.
        </Alert>
        <Grid container spacing={2} sx={{ maxWidth: 640 }}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={STATUS_OPTIONS}
              value={roomStatuses}
              onChange={(e, val) => setRoomStatuses(val)}
              renderInput={(params) => (
                <TextField {...params} label="Default Room Statuses" placeholder="Add status" />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={issueCategories}
              onChange={(e, val) => setIssueCategories(val)}
              renderInput={(params) => (
                <TextField {...params} label="Default Issue Categories" placeholder="Add category" />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={itemCategories}
              onChange={(e, val) => setItemCategories(val)}
              renderInput={(params) => (
                <TextField {...params} label="Default Item Categories" placeholder="Add category" />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            {saveBtn("defaults", () =>
              save("defaults", {
                defaults: { roomStatuses, issueCategories, itemCategories },
              })
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* 6 · Billing Contacts */}
      <TabPanel value={tab} index={5}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
          People who should receive billing communications. Shown on the Billing page.
        </Typography>
        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          {contacts.length === 0 && (
            <Typography color="text.disabled">No billing contacts yet.</Typography>
          )}
          {contacts.map((c, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={c.name || ""}
                    onChange={(e) =>
                      setContacts((cs) =>
                        cs.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x))
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={c.email || ""}
                    onChange={(e) =>
                      setContacts((cs) =>
                        cs.map((x, idx) => (idx === i ? { ...x, email: e.target.value } : x))
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => setContacts((cs) => cs.filter((_, idx) => idx !== i))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setContacts((cs) => [...cs, { name: "", email: "" }])}
            >
              Add Contact
            </Button>
          </Box>
          <Divider />
          <Box>
            {saveBtn("billing", () =>
              save("billing", {
                billing: { contacts: contacts.filter((c) => c.name || c.email) },
              })
            )}
          </Box>
        </Stack>
      </TabPanel>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
